/**
 * SecurityTab — passkeys, linked mobile devices.
 */

import { useCallback, useEffect, useState } from "react";
import { authFetchRaw } from "@/lib/api";
import { registerPasskey } from "@/lib/passkey";

interface Passkey {
  id: string;
  device_name: string;
  aaguid: string;
  backed_up: boolean;
  created_at: string;
  last_used_at: string | null;
  transports: string[];
}

interface LinkedDevice {
  id: string;
  device_name: string;
  platform: string;
  created_at: string;
}

export function SecurityTab() {
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [devices, setDevices] = useState<LinkedDevice[]>([]);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [pkResp, devResp] = await Promise.all([
        authFetchRaw("/api/passkey/list"),
        authFetchRaw("/api/devices/list"),
      ]);
      if (pkResp.ok) setPasskeys((await pkResp.json()) as Passkey[]);
      if (devResp.ok) setDevices((await devResp.json()) as LinkedDevice[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Beveiligingsgegevens laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const addPasskey = useCallback(async () => {
    const label = window.prompt("Naam voor deze passkey:");
    if (!label) return;
    setError("");
    try {
      await registerPasskey(label.trim());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passkey toevoegen mislukt.");
    }
  }, [load]);

  const deletePasskey = useCallback(async (id: string) => {
    setError("");
    try {
      const resp = await authFetchRaw(`/api/passkey/${id}`, { method: "DELETE" });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { message?: string; detail?: string };
        setError(body.detail ?? body.message ?? `Verwijderen mislukt (${resp.status})`);
        return;
      }
      setPasskeys((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verwijderen mislukt.");
    }
  }, []);

  const startRename = useCallback((pk: Passkey) => {
    setRenamingId(pk.id);
    setRenameValue(pk.device_name);
  }, []);

  const saveRename = useCallback(
    async (id: string) => {
      setRenameBusy(true);
      setError("");
      try {
        const resp = await authFetchRaw(`/api/passkey/${id}`, {
          method: "PATCH",
          body: JSON.stringify({ device_name: renameValue.trim() }),
        });
        if (!resp.ok) {
          const body = (await resp.json().catch(() => ({}))) as { message?: string; detail?: string };
          setError(body.detail ?? body.message ?? `Opslaan mislukt (${resp.status})`);
          return;
        }
        setPasskeys((prev) =>
          prev.map((p) => (p.id === id ? { ...p, device_name: renameValue.trim() } : p)),
        );
        setRenamingId(null);
      } finally {
        setRenameBusy(false);
      }
    },
    [renameValue],
  );

  const revokeDevice = useCallback(async (id: string) => {
    setError("");
    try {
      const resp = await authFetchRaw(`/api/devices/${id}`, { method: "DELETE" });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { message?: string; detail?: string };
        setError(body.detail ?? body.message ?? `Intrekken mislukt (${resp.status})`);
        return;
      }
      setDevices((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Intrekken mislukt.");
    }
  }, []);

  if (loading) {
    return <p className="text-sm text-slate-500">Laden…</p>;
  }

  return (
    <div className="space-y-6">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {/* Passkeys */}
      <section className="glass-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-slate-900">Passkeys</h2>
          <button
            type="button"
            onClick={() => void addPasskey()}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            + Passkey toevoegen
          </button>
        </div>

        {passkeys.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nog geen passkey ingesteld. Voeg er een toe om veilig in te loggen zonder wachtwoord.
          </p>
        ) : (
          <div className="space-y-3">
            {passkeys.map((pk) => (
              <div
                key={pk.id}
                className="rounded-xl border border-slate-100 bg-slate-50 p-4"
              >
                {renamingId === pk.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => void saveRename(pk.id)}
                      disabled={renameBusy || !renameValue.trim()}
                      className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
                    >
                      {renameBusy ? "…" : "Opslaan"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRenamingId(null)}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Annuleer
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{pk.device_name}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {pk.backed_up ? "Cloud-backup aan" : "Lokale passkey"}
                        {pk.last_used_at
                          ? ` · Laatst gebruikt ${new Date(pk.last_used_at).toLocaleDateString("nl-BE")}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => startRename(pk)}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Hernoemen
                      </button>
                      <button
                        type="button"
                        onClick={() => void deletePasskey(pk.id)}
                        className="rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                      >
                        Verwijderen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Linked mobile devices */}
      <section className="glass-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-bold text-slate-900">Mobiele apparaten</h2>

        {devices.length === 0 ? (
          <p className="text-sm text-slate-500">Geen gekoppelde apparaten.</p>
        ) : (
          <div className="space-y-3">
            {devices.map((dev) => (
              <div
                key={dev.id}
                className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-4"
              >
                <div>
                  <p className="font-medium text-slate-900">{dev.device_name}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {dev.platform} · Toegevoegd{" "}
                    {new Date(dev.created_at).toLocaleDateString("nl-BE")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void revokeDevice(dev.id)}
                  className="rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                >
                  Intrekken
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
